import React, { useEffect, useState } from 'react';
import './termsConsentModal.css';
import { CandidateService } from '../../../services/candidateService';

const TermsConsentModal = ({ show, onAccepted }) => {
    const [consentChecked, setConsentChecked] = useState(false);
    const [modalType, setModalType] = useState('TERMS');
    const [errorMessage, setErrorMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (show) {
            setModalType('TERMS');
            setConsentChecked(false);
            setErrorMessage('');
        }
    }, [show]);

    const handleAccept = async () => {
        if (!consentChecked || isProcessing) {
            return;
        }
        try {
            setIsProcessing(true);
            setErrorMessage('');
            await CandidateService.acceptTerms();
            localStorage.setItem('termsStatus', 'ACCEPTED');
            setConsentChecked(false);
            setModalType(null);
            if (onAccepted) {
                onAccepted();
            }

        } catch (error) {
            console.error(
                'Failed to accept Terms and Conditions:',
                error
            );

            setErrorMessage(
                error?.response?.data?.message ||
                'Failed to accept Terms and Conditions. Please try again.'
            );

        } finally {
            setIsProcessing(false);
        }
    };
    const handleDecline = () => {
        setErrorMessage('');
        setModalType('CONFIRM_DECLINE');
    };
    const confirmDecline = async () => {
        if (isProcessing) {
            return;
        }
        try {
            setIsProcessing(true);
            setErrorMessage('');
            await CandidateService.declineTerms();
            localStorage.setItem('termsStatus', 'DECLINED');
            setConsentChecked(false);
            setModalType('DECLINED');
        } catch (error) {
            console.error(
                'Failed to decline Terms and Conditions:',
                error
            );

            setErrorMessage(
                error?.response?.data?.message ||
                'Unable to process your request. Please try again.'
            );

        } finally {
            setIsProcessing(false);
        }
    };

    const handleLogout = async () => {
        if (isProcessing) {
            return;
        }

        try {
            setIsProcessing(true);
            await CandidateService.logout();

        } catch (error) {
            console.error('Logout failed:', error);

        } finally {
            localStorage.removeItem('authToken');
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
        }
    };

    const handleAcceptTermsAgain = () => {
        setErrorMessage('');
        setConsentChecked(false);
        setModalType('TERMS');
    };

    if (!show) {
        return null;
    }

    if (modalType === 'CONFIRM_DECLINE') {
        return (
            <div className="terms-consent-overlay">
                <div className="terms-action-modal">
                    <div className="terms-action-modal-content">
                        <h2>Decline Terms & Conditions?</h2>
                        <p>
                            If you decline the Terms & Conditions, you will not be
                            able to access your candidate profile or use the platform
                            until you accept the Terms & Conditions.
                        </p>
                        {errorMessage && (
                            <div className="terms-consent-error">
                                {errorMessage}
                            </div>
                        )}
                        <div className="terms-action-modal-buttons">
                            <button
                                type="button"
                                className="terms-cancel-button"
                                onClick={() => {
                                    setErrorMessage('');
                                    setModalType('TERMS');
                                }}
                                disabled={isProcessing}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="terms-decline-confirm-button"
                                onClick={confirmDecline}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : 'Decline'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (modalType === 'DECLINED') {
        return (
            <div className="terms-consent-overlay">
                <div className="terms-action-modal">
                    <div className="terms-action-modal-content">
                        <h2>Access Restricted</h2>
                        <p>
                            You cannot access the platform until you accept the
                            Terms & Conditions.
                        </p>

                        <p>
                            Please accept the Terms & Conditions to continue using
                            your candidate profile.
                        </p>

                        <div className="terms-action-modal-buttons">
                            <button
                                type="button"
                                className="terms-logout-button"
                                onClick={handleLogout}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Logging out...' : 'Logout'}
                            </button>

                            <button
                                type="button"
                                className="terms-accept-again-button"
                                onClick={handleAcceptTermsAgain}
                                disabled={isProcessing}
                            >
                                Accept Terms
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="terms-consent-overlay">
            {errorMessage && (
                <div className="terms-consent-error">
                    {errorMessage}
                </div>
            )}

            <div className="terms-consent-modal">
                <div className="terms-consent-modal-header">
                    <p>
                        Please read the Privacy Policy and Terms & Conditions carefully.
                    </p>
                </div>

                <div className="terms-consent-policy-content">
                    <h2>Terms & Conditions</h2>
                    <p>
                        Welcome to Signite Technology Pvt Ltd (“we,” “us,” “our”).
                        These Terms of Service (“Terms”) govern your access to and
                        use of our Software-as-a-Service (SaaS) web platform
                        (“Service”), which is provided by Signite Technology Pvt Ltd,
                        a company registered under the laws of India.
                    </p>

                    <p>
                        By accessing or using our Service, you agree to be bound by
                        these Terms. If you do not agree to these Terms, you must not
                        use the Service.
                    </p>

                    <h2>1. Acceptance of Terms</h2>

                    <p>
                        By accessing or using the Service, you agree to comply with
                        and be bound by these Terms.
                    </p>

                    <h2>2. Use of the Service</h2>

                    <p>
                        To use the Service, you must be at least 18 years old and
                        capable of forming legally binding contracts under applicable
                        law.
                    </p>

                    <p>
                        You must create an account to access and use the Service.
                        You are responsible for maintaining the confidentiality of
                        your account credentials.
                    </p>

                    <h2>3. Subscription and Payment</h2>

                    <p>
                        The Service may be provided under different subscription
                        plans. By subscribing to a plan, you agree to pay the fees
                        associated with that plan.
                    </p>

                    <h2>4. Intellectual Property</h2>

                    <p>
                        All intellectual property rights in the Service and its
                        content are owned by Signite Technology Pvt Ltd or its
                        licensors.
                    </p>

                    <h2>5. Privacy and Data Protection</h2>

                    <p>
                        Your use of the Service is governed by our Privacy Policy,
                        which explains how we collect, use, and protect your personal
                        data.
                    </p>

                    <h2>6. Confidentiality</h2>

                    <p>
                        You agree to maintain the confidentiality of information
                        marked as confidential by Signite Technology Pvt Ltd.
                    </p>

                    <h2>7. Limitation of Liability</h2>

                    <p>
                        The Service is provided "as is" and "as available," without
                        warranties except where required by applicable law.
                    </p>

                    <h2>8. Indemnification</h2>

                    <p>
                        You agree to indemnify and hold harmless Signite Technology
                        Pvt Ltd from claims arising from your violation of these
                        Terms.
                    </p>

                    <h2>9. Termination and Suspension</h2>

                    <p>
                        We may suspend or terminate your access to the Service if you
                        violate these Terms or engage in conduct that threatens the
                        security or integrity of the Service.
                    </p>

                    <h2>10. Governing Law and Dispute Resolution</h2>

                    <p>
                        These Terms are governed by and construed in accordance with
                        the laws of India.
                    </p>

                    <h2>11. Miscellaneous</h2>

                    <p>
                        These Terms, along with other legal notices and policies
                        published by Signite Technology Pvt Ltd, constitute the
                        agreement regarding use of the Service.
                    </p>

                    <h2>12. Contact Information</h2>

                    <p>
                        For questions regarding these Terms, please contact{' '}
                        <a
                            href="mailto:admin@leaderly.in"
                            className="terms-consent-contact-link"
                        >
                            admin@leaderly.in
                        </a>.
                    </p>

                    <h2>Privacy Policy</h2>

                    <p>
                        At Signite Technology Pvt Ltd, we respect your privacy and
                        are committed to protecting the personal information you
                        share with us.
                    </p>

                    <h2>1. Information We Collect</h2>

                    <p>
                        We may collect personal, non-personal and account information
                        when you use our platform.
                    </p>

                    <h2>2. How We Use Your Information</h2>

                    <p>
                        We use information to provide, maintain and improve our
                        services, provide customer support, process payments and
                        comply with legal obligations.
                    </p>

                    <h2>3. How We Protect Your Information</h2>

                    <p>
                        We implement reasonable technical and organizational measures
                        to protect your information.
                    </p>

                    <h2>4. Cookies and Tracking Technologies</h2>

                    <p>
                        We use cookies and other technologies to understand how
                        users interact with our platform.
                    </p>

                    <h2>5. Sharing and Disclosure</h2>

                    <p>
                        Information may be shared with trusted service providers and
                        where legally required.
                    </p>

                    <h2>6. Your Rights and Choices</h2>

                    <p>
                        You may request access, correction or deletion of your
                        personal information subject to applicable laws.
                    </p>

                    <h2>7. Data Retention</h2>

                    <p>
                        We retain information for as long as necessary for the
                        purposes described in this Privacy Policy.
                    </p>

                    <h2>8. Third-Party Links</h2>

                    <p>
                        Our platform may contain links to third-party websites.
                    </p>

                    <h2>9. Changes to this Privacy Policy</h2>

                    <p>
                        We may update this Privacy Policy from time to time.
                    </p>

                    <h2>10. Contact Us</h2>

                    <p>
                        For questions regarding this Privacy Policy, please contact{' '}
                        <a
                            href="mailto:admin@leaderly.in"
                            className="terms-consent-contact-link"
                        >
                            admin@leaderly.in
                        </a>.
                    </p>

                </div>

                <div className="terms-consent-footer">
                    <label className="terms-consent-checkbox">
                        <input
                            type="checkbox"
                            checked={consentChecked}
                            onChange={(event) =>
                                setConsentChecked(event.target.checked)
                            }
                        />
                        <span>
                            I have read and agree to the Privacy Policy and
                            Terms & Conditions.
                        </span>
                    </label>

                    <div className="terms-consent-actions">

                        <button
                            type="button"
                            className="terms-decline-button"
                            onClick={handleDecline}
                        >
                            Decline
                        </button>

                        <button
                            type="button"
                            className="terms-accept-button"
                            disabled={!consentChecked}
                            onClick={handleAccept}
                        >
                            Accept
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsConsentModal;